import React, { useEffect, useMemo, useState } from "react";
import { Download, Share, PlusSquare, X, Sparkles, Menu, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  hasDeferredPrompt,
  isIOSSafari,
  isStandalone,
  onInstallStateChange,
  platform,
  setInstallDecision,
  shouldPromptInstall,
  triggerNativeInstall,
} from "@/lib/install";

const COPY = {
  en: {
    kicker: "Before you scroll",
    title: "Slip us into your pocket",
    body: "This site travels better as an app. It\u2019s important for the wedding \u2014 installing puts the countdown, your RSVP and Andreas\u2019 little reminders one tap away, wherever you are. Siga siga, it takes ten seconds.",
    highlight: "Notifications only work once installed on iPhone. Apple insists.",
    installBtn: "Install Eli & Mag",
    laterBtn: "Maybe later",
    dismissBtn: "Not for me",
    installed: "Installed. Beautiful, koumbare.",
    later: "Alright, we\u2019ll ask again later.",
    dismissed: "No worries \u2014 you can install any time from your browser menu.",
    ios: {
      title: "One quick step, koumbare",
      body: "To turn this site into an app, tap the Share icon below, then choose Add to Home Screen. That way Andreas can actually reach you \u2014 Apple insists.",
      step1: "Tap the Share icon",
      step1sub: "at the bottom of Safari",
      step2: "Choose \u201cAdd to Home Screen\u201d",
      step2sub: "then tap Add \u2014 done",
      gotIt: "Got it",
    },
    android: {
      title: "Almost there, koumbare",
      body: "Your browser hasn\u2019t offered the install button yet. Open the browser menu and pick Install app (or Add to Home Screen).",
      step1: "Tap the menu icon",
      step1sub: "three dots at the top of Chrome",
      step2: "Tap \u201cInstall app\u201d",
      step2sub: "or Add to Home Screen",
      gotIt: "Got it",
    },
  },
  fr: {
    kicker: "Avant de faire défiler",
    title: "Glisse-nous dans ta poche",
    body: "Ce site voyage mieux en app. C\u2019est important pour le mariage \u2014 l\u2019installer met le compte à rebours, ton RSVP et les petits rappels d\u2019Andreas à un doigt d\u2019écart, où que tu sois. Siga siga, ça prend dix secondes.",
    highlight: "Sur iPhone, les notifications n\u2019arrivent qu\u2019une fois l\u2019app installée. Apple insiste.",
    installBtn: "Installer Eli & Mag",
    laterBtn: "Peut-être plus tard",
    dismissBtn: "Non merci",
    installed: "Installé. Magnifique, koumbare.",
    later: "D\u2019accord, on te redemandera plus tard.",
    dismissed: "Pas de souci \u2014 tu peux installer plus tard depuis le menu du navigateur.",
    ios: {
      title: "Un petit geste, koumbare",
      body: "Pour transformer ce site en app, touche l\u2019icône Partager en bas, puis choisis « Sur l\u2019écran d\u2019accueil ». C\u2019est comme ça qu\u2019Andreas peut vraiment te joindre \u2014 Apple insiste.",
      step1: "Touche l\u2019icône Partager",
      step1sub: "en bas de Safari",
      step2: "Choisis « Sur l\u2019écran d\u2019accueil »",
      step2sub: "puis touche Ajouter",
      gotIt: "Compris",
    },
    android: {
      title: "Presque installé, koumbare",
      body: "Ton navigateur n\u2019a pas encore proposé le bouton. Ouvre le menu du navigateur et choisis « Installer l\u2019application » (ou « Ajouter à l\u2019écran d\u2019accueil »).",
      step1: "Touche le menu",
      step1sub: "les trois points en haut de Chrome",
      step2: "Choisis « Installer l\u2019app »",
      step2sub: "ou Ajouter à l\u2019écran d\u2019accueil",
      gotIt: "Compris",
    },
  },
  el: {
    kicker: "Πριν προχωρήσεις",
    title: "Βάλε μας στην τσέπη σου",
    body: "Το site ταξιδεύει καλύτερα σαν εφαρμογή. Είναι σημαντικό για τον γάμο \u2014 η εγκατάσταση φέρνει την αντίστροφη μέτρηση, το RSVP σου και τις υπενθυμίσεις του Andreas ένα άγγιγμα μακριά. Σιγά σιγά, δέκα δευτερόλεπτα φτάνουν.",
    highlight: "Στο iPhone οι ειδοποιήσεις δουλεύουν μόνο μετά την εγκατάσταση. Η Apple το ζητάει.",
    installBtn: "Εγκατάσταση Eli & Mag",
    laterBtn: "Ίσως αργότερα",
    dismissBtn: "Όχι για μένα",
    installed: "Εγκαταστάθηκε. Τέλειο, κουμπάρε.",
    later: "Εντάξει, θα σε ρωτήσουμε αργότερα.",
    dismissed: "Κανένα πρόβλημα \u2014 μπορείς να εγκαταστήσεις ανά πάσα στιγμή από το μενού του browser.",
    ios: {
      title: "Μια κίνηση, κουμπάρε",
      body: "Για να γίνει εφαρμογή, πάτα το εικονίδιο Κοινοποίηση κάτω, μετά διάλεξε «Προσθήκη στην αρχική οθόνη». Έτσι θα σε βρίσκει ο Andreas.",
      step1: "Πάτα το εικονίδιο Κοινοποίηση",
      step1sub: "στο κάτω μέρος του Safari",
      step2: "Διάλεξε «Προσθήκη στην αρχική»",
      step2sub: "μετά πάτα Προσθήκη",
      gotIt: "Κατάλαβα",
    },
    android: {
      title: "Σχεδόν έτοιμο, κουμπάρε",
      body: "Ο browser δεν έχει προτείνει ακόμα το κουμπί. Άνοιξε το μενού του browser και διάλεξε «Εγκατάσταση εφαρμογής» (ή «Προσθήκη στην αρχική οθόνη»).",
      step1: "Πάτα το μενού",
      step1sub: "τις τρεις τελείες πάνω στο Chrome",
      step2: "Διάλεξε «Εγκατάσταση εφαρμογής»",
      step2sub: "ή «Προσθήκη στην αρχική»",
      gotIt: "Κατάλαβα",
    },
  },
  pl: {
    kicker: "Zanim przewiniesz",
    title: "Wsuń nas do kieszeni",
    body: "Ta strona lepiej podróżuje jako aplikacja. To ważne dla wesela \u2014 instalacja daje ci odliczanie, RSVP i przypomnienia Andreasa jednym dotknięciem. Siga siga, dziesięć sekund wystarczy.",
    highlight: "Na iPhonie powiadomienia działają dopiero po instalacji. Apple wymaga.",
    installBtn: "Zainstaluj Eli & Mag",
    laterBtn: "Może później",
    dismissBtn: "Nie dla mnie",
    installed: "Zainstalowane. Pięknie, kumie.",
    later: "Dobrze, zapytamy później.",
    dismissed: "Żadnego problemu \u2014 możesz zainstalować później z menu przeglądarki.",
    ios: {
      title: "Jeden krok, kumie",
      body: "Żeby przerobić stronę na aplikację, dotknij ikony Udostępnij na dole Safari, potem wybierz «Do ekranu głównego». Tylko tak Andreas do ciebie dotrze \u2014 Apple wymaga.",
      step1: "Dotknij ikony Udostępnij",
      step1sub: "na dole Safari",
      step2: "Wybierz «Do ekranu głównego»",
      step2sub: "potem stuknij Dodaj",
      gotIt: "Jasne",
    },
    android: {
      title: "Prawie gotowe, kumie",
      body: "Przeglądarka jeszcze nie zaproponowała przycisku. Otwórz menu przeglądarki i wybierz «Zainstaluj aplikację» (lub «Dodaj do ekranu głównego»).",
      step1: "Dotknij menu",
      step1sub: "trzy kropki u góry Chrome",
      step2: "Wybierz «Zainstaluj aplikację»",
      step2sub: "albo «Do ekranu głównego»",
      gotIt: "Jasne",
    },
  },
};

const InstallPromptModal = () => {
  const { lang } = useLang();
  const { isAuthed, authReady } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [tick, setTick] = useState(0); // forces re-eval of hasDeferredPrompt()

  const c = useMemo(() => COPY[lang] || COPY.en, [lang]);
  const plat = platform();
  // Pick platform-appropriate fallback tutorial copy.
  const fb = plat === "ios" ? c.ios : (c.android || c.ios);

  useEffect(() => onInstallStateChange(() => setTick((n) => n + 1)), []);

  useEffect(() => {
    if (!authReady || !isAuthed) return;
    if (isStandalone()) return; // Already installed — nothing to do.
    // First-visit friendly delay (shorter than notification modal).
    const t = setTimeout(() => {
      if (shouldPromptInstall()) setOpen(true);
    }, 1200);
    return () => clearTimeout(t);
  }, [authReady, isAuthed]);

  const onInstall = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (plat === "ios") {
        setShowFallback(true);
        return;
      }
      if (hasDeferredPrompt()) {
        const res = await triggerNativeInstall();
        if (res.ok) {
          toast.success(c.installed);
          setOpen(false);
        } else if (res.outcome === "dismissed") {
          toast(c.dismissed);
          setOpen(false);
        } else {
          // Event not yet fired — fall back to a platform-specific tutorial.
          setShowFallback(true);
        }
      } else {
        // Some Chromiums delay the event; show the platform-specific hint.
        setShowFallback(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const onLater = () => {
    setInstallDecision("later");
    setOpen(false);
  };

  const onDismiss = () => {
    setInstallDecision("dismissed");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[290] flex items-end sm:items-center justify-center bg-foreground/45 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid="install-modal"
    >
      <div className="relative w-full max-w-md bg-background border border-border rounded-2xl shadow-2xl overflow-hidden">
        <button
          type="button"
          onClick={onLater}
          className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center rounded-md text-foreground/60 hover:text-foreground hover:bg-foreground/5 z-10"
          aria-label="Close"
          data-testid="install-close"
        >
          <X className="w-4 h-4" />
        </button>

        {!showFallback ? (
          <div className="p-6 sm:p-7">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-foreground text-background flex items-center justify-center shrink-0 shadow-md">
                <span className="font-serif-display text-lg leading-none">E&M</span>
              </div>
              <div className="min-w-0">
                <p className="editorial-kicker text-foreground/50" data-testid="install-kicker">{c.kicker}</p>
                <p className="font-serif-display text-[13px] text-foreground/70">19 · 05 · 2027 · Cyprus</p>
              </div>
            </div>

            <h2 className="font-serif-display text-2xl sm:text-[28px] leading-[1.15]" data-testid="install-title">
              {c.title}
            </h2>
            <p className="text-sm sm:text-[15px] leading-relaxed text-foreground/75 mt-3" data-testid="install-body">{c.body}</p>

            <div className="mt-4 flex items-start gap-2 rounded-md border border-accent/30 bg-accent/[0.08] px-3 py-2">
              <Sparkles className="w-3.5 h-3.5 text-accent mt-[3px] shrink-0" />
              <p className="text-[12px] leading-snug text-foreground/80">{c.highlight}</p>
            </div>

            <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={onLater}
                className="h-11 px-4 rounded-lg border border-border text-foreground/70 hover:text-foreground hover:border-foreground/40 transition-colors text-[12px] tracking-[0.14em] uppercase"
                data-testid="install-later"
              >
                {c.laterBtn}
              </button>
              <button
                type="button"
                onClick={onDismiss}
                className="h-11 px-4 rounded-lg text-foreground/50 hover:text-foreground/80 transition-colors text-[12px] tracking-[0.14em] uppercase sm:mr-1"
                data-testid="install-dismiss"
              >
                {c.dismissBtn}
              </button>
              <button
                type="button"
                onClick={onInstall}
                disabled={busy}
                className="h-11 px-5 rounded-lg bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60 transition-colors text-[12px] tracking-[0.14em] uppercase inline-flex items-center justify-center gap-2"
                data-testid="install-accept"
              >
                <Download className="w-4 h-4" />
                {c.installBtn}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 sm:p-7" data-testid={plat === "ios" ? "install-ios-guide" : "install-android-guide"}>
            <p className="editorial-kicker text-foreground/50">{c.kicker}</p>
            <h2 className="font-serif-display text-2xl sm:text-[28px] leading-[1.15] mt-1">{fb.title}</h2>
            <p className="text-sm text-foreground/75 mt-3">{fb.body}</p>

            {/* Step 1 */}
            <div className="mt-5 flex items-center gap-3 rounded-lg border border-border p-3 bg-background">
              <div className="w-10 h-10 rounded-md bg-foreground/5 text-foreground flex items-center justify-center shrink-0">
                {plat === "ios" ? <Share className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium">1 · {fb.step1}</p>
                <p className="text-[12px] text-foreground/60">{fb.step1sub}</p>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex justify-center py-1">
              <ArrowDown className="w-4 h-4 text-foreground/30 animate-bounce" aria-hidden="true" />
            </div>

            {/* Step 2 */}
            <div className="flex items-center gap-3 rounded-lg border border-border p-3 bg-background">
              <div className="w-10 h-10 rounded-md bg-foreground/5 text-foreground flex items-center justify-center shrink-0">
                {plat === "ios" ? <PlusSquare className="w-5 h-5" /> : <Download className="w-5 h-5" />}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium">2 · {fb.step2}</p>
                <p className="text-[12px] text-foreground/60">{fb.step2sub}</p>
              </div>
            </div>

            {/* Bottom mockup — Safari share bar for iOS, Chrome toolbar for Android */}
            {plat === "ios" ? (
              <div className="relative mt-5 h-16 rounded-lg border border-border bg-foreground/[0.03] flex items-center justify-around px-4" aria-hidden="true">
                <span className="text-foreground/30 text-lg">←</span>
                <span className="text-foreground/30 text-lg">→</span>
                <span className="relative text-foreground text-lg">
                  <Share className="w-5 h-5" />
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-accent text-xl animate-bounce">↓</span>
                </span>
                <span className="text-foreground/30 text-lg">□</span>
                <span className="text-foreground/30 text-lg">☰</span>
              </div>
            ) : (
              <div className="relative mt-5 h-16 rounded-lg border border-border bg-foreground/[0.03] flex items-center justify-between px-4" aria-hidden="true">
                <span className="text-foreground/30 text-sm">Aa</span>
                <span className="text-foreground/30 text-sm truncate max-w-[40%]">eliandmag.wedding</span>
                <span className="text-foreground/30 text-lg">↻</span>
                <span className="relative text-foreground text-lg">
                  <Menu className="w-5 h-5" />
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-accent text-xl animate-bounce">↓</span>
                </span>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={onLater}
                className="h-11 px-5 rounded-lg bg-foreground text-background hover:bg-foreground/90 transition-colors text-[12px] tracking-[0.14em] uppercase"
                data-testid="install-fallback-gotit"
              >
                {fb.gotIt}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstallPromptModal;
